import { Edit2, Trash2, MapPin, Star, Bed, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Competitor, OTA_CHANNEL_COLORS } from '@/types/competitor';
import { OTAChannel } from '@/types/revenue-manager';

interface CompetitorCardProps {
  competitor: Competitor;
  otaChannels: OTAChannel[];
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}

const DEFAULT_OTA_NAMES: Record<string, string> = {
  'booking': 'Booking.com',
  'makemytrip': 'Expedia',
  // 'hotels': 'Hotels.com',
  // 'airbnb': 'Airbnb',
  // 'agoda': 'Agoda',
  // 'tripadvisor': 'TripAdvisor',
};

const DEFAULT_OTA_COLORS: Record<string, string> = {
  'booking': '#003580',
  'makemytrip': '#F7B500',
  // 'hotels': '#D32F2F',
  // 'airbnb': '#FF5A5F',
  // 'agoda': '#7B2EA0',
  // 'tripadvisor': '#34E0A1',
};

export function CompetitorCard({
  competitor,
  otaChannels,
  onEdit,
  onDelete,
  onToggle,
}: CompetitorCardProps) {
  const getOTAName = (otaId: string) => {
    const channel = otaChannels.find(c => c.id === otaId);
    return channel?.name || DEFAULT_OTA_NAMES[otaId] || otaId;
  };

  const getOTAColor = (otaId: string) => {
    const channel = otaChannels.find(c => c.id === otaId);
    const name = channel?.name?.toLowerCase() || otaId;
    return OTA_CHANNEL_COLORS[name] || DEFAULT_OTA_COLORS[otaId] || '#666';
  };

  const categoryLabel: Record<string, string> = {
    budget: 'Budget',
    'mid-range': 'Mid-Range',
    luxury: 'Luxury',
  };

  const marketPositionColors = {
    budget: 'bg-green-100 text-green-700 border-green-200',
    'mid-range': 'bg-blue-100 text-blue-700 border-blue-200',
    luxury: 'bg-purple-100 text-purple-700 border-purple-200',
  };

  return (
    <Card className={cn(
      'transition-all duration-200',
      !competitor.is_active && 'opacity-60'
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold">{competitor.name}</CardTitle>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {competitor.location}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={competitor.is_active}
              onCheckedChange={onToggle}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Row - Only displayed when at least one stat is available */}
        {(competitor.starRating || competitor.rooms || competitor.distance) && (
          <div className="flex items-center gap-4 text-sm">
            {competitor.starRating && (
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                <span>{competitor.starRating} Star{Number(competitor.starRating) !== 1 && 's'}</span>
              </div>
            )}
            {competitor.rooms && (
              <div className="flex items-center gap-1">
                <Bed className="h-4 w-4 text-muted-foreground" />
                <span>{competitor.rooms} rooms</span>
              </div>
            )}
            {competitor.distance && (
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{competitor.distance} {competitor.distanceUnit === 'km' ? 'km' : 'mi'}</span>
              </div>
            )}
          </div>
        )}

        {/* Category */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary">{categoryLabel[competitor.category]}</Badge>
          <Badge variant="outline" className={marketPositionColors[competitor.category]}>
            {competitor.category}
          </Badge>
        </div>

        {/* OTA Channels */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Tracking on:</p>
          <div className="flex flex-wrap gap-1.5">
            {(competitor.trackedOTAs || []).map(otaId => (
              <Badge
                key={otaId}
                variant="outline"
                className="text-xs"
                style={{ borderColor: getOTAColor(otaId), color: getOTAColor(otaId) }}
              >
                <span
                  className="mr-1.5 h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: getOTAColor(otaId) }}
                />
                {getOTAName(otaId)}
              </Badge>
            ))}
          </div>
        </div>

        {/* Amenities */}
        {competitor.amenities.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Amenities:</p>
            <p className="text-sm text-muted-foreground">{competitor.amenities.join(', ')}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <Button variant="ghost" size="sm" onClick={onEdit} className="flex-1">
            <Edit2 className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="flex-1 text-destructive hover:text-destructive">
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
